import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api, type OrgInfo, type AccessGrant, type Collection } from '../lib/api';
import AppHeader from '../components/AppHeader';

type CollectionCard = {
  id: string;
  orgSlug: string;
  orgName: string;
  collectionSlug: string;
  collectionName: string;
  groupName: string | null;
  role: 'Owner' | 'Admin' | 'Viewer';
};

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [collectionCards, setCollectionCards] = useState<CollectionCard[]>([]);
  const [ownedOrgSlugs, setOwnedOrgSlugs] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    async function loadDashboard() {
      try {
        const [orgs, grants] = await Promise.all([
          api.me.organizations(),
          api.me.access(),
        ]);

        setOwnedOrgSlugs(new Set(orgs.map(o => o.slug)));

        // Fetch collections for all owned orgs in parallel
        const collectionsPerOrg = await Promise.all(
          orgs.map(async (org: OrgInfo) => ({
            org,
            collections: await api.orgs.collections(org.slug).catch((): Collection[] => []),
          }))
        );

        const cards: CollectionCard[] = [];

        // Add owned collections
        for (const { org, collections } of collectionsPerOrg) {
          for (const col of collections) {
            cards.push({
              id: `${org.id}-${col.id}`,
              orgSlug: org.slug,
              orgName: org.display_name,
              collectionSlug: col.slug,
              collectionName: col.name,
              groupName: col.group_name,
              role: 'Owner',
            });
          }
        }

        // Add granted collections (skip orgs the user owns — already included above)
        const ownedSlugs = new Set(orgs.map(o => o.slug));
        for (const grant of grants as AccessGrant[]) {
          if (!ownedSlugs.has(grant.org_slug)) {
            cards.push({
              id: `${grant.org_id}-${grant.collection_id}`,
              orgSlug: grant.org_slug,
              orgName: grant.org_display_name,
              collectionSlug: grant.collection_slug,
              collectionName: grant.collection_name,
              groupName: grant.group_name,
              role: grant.level === 'ADMIN' ? 'Admin' : 'Viewer',
            });
          }
        }

        setCollectionCards(cards);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    }

    loadDashboard();
  }, [user, navigate]);

  // Group by group_name (or org name as fallback)
  const groupedCards = useMemo(() => {
    const groups: Record<string, {
      label: string;
      orgName: string;
      orgSlug: string;
      cards: CollectionCard[];
    }> = {};

    for (const card of collectionCards) {
      const key = `${card.orgSlug}/${card.groupName ?? '__default__'}`;
      if (!groups[key]) {
        groups[key] = {
          label: card.groupName ?? card.orgName,
          orgName: card.orgName,
          orgSlug: card.orgSlug,
          cards: [],
        };
      }
      groups[key].cards.push(card);
    }

    return groups;
  }, [collectionCards]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const getRoleBadgeStyles = (role: CollectionCard['role']) => {
    switch (role) {
      case 'Owner': return 'bg-primary/10 text-primary';
      case 'Admin': return 'bg-purple-500/10 text-purple-600 dark:text-purple-400';
      case 'Viewer': return 'bg-green-500/10 text-green-600 dark:text-green-400';
    }
  };

  const getRoleIcon = (role: CollectionCard['role']) => {
    switch (role) {
      case 'Owner': return '👑';
      case 'Admin': return '⚡';
      case 'Viewer': return '👁️';
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-4 lg:px-8 py-8 w-full">
        <div className="bg-card rounded-xl shadow-sm border border-border p-8">
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Welcome back, {user.email?.split('@')[0] || 'there'}!
            </h2>
            <p className="text-muted-foreground">Ready to pick up where you left off? Let's make your documentation shine today.</p>
          </div>

          {collectionCards.length > 0 ? (
            <div className="space-y-10">
              {Object.entries(groupedCards).map(([key, group]) => (
                <section key={key}>
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-foreground">{group.label}</h3>
                    <p className="text-sm text-muted-foreground">{group.orgName}</p>
                  </div>

                  <div className="flex flex-col gap-3">
                    {group.cards.map((card) => (
                      <div
                        key={card.id}
                        onClick={() => navigate(`/${card.orgSlug}/${card.collectionSlug}`)}
                        className="w-full bg-background rounded-lg p-4 hover:shadow-md transition border border-border flex items-center justify-between gap-4 cursor-pointer"
                      >
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <h4 className="text-base font-semibold text-foreground truncate">{card.collectionName}</h4>
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getRoleBadgeStyles(card.role)}`}>
                            {getRoleIcon(card.role)} {card.role}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="flex items-center text-sm font-semibold text-primary">
                            View Docs
                            <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </span>

                          {card.role === 'Owner' && ownedOrgSlugs.has(card.orgSlug) && (
                            <Link
                              to={`/admin/${card.orgSlug}/${card.collectionSlug}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 py-2 px-3 rounded-lg transition"
                              title="Manage Access"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 mb-6">
                <svg className="h-8 w-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Welcome to Smartdoxx!</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Your account has been created successfully. You don't have access to any documentation yet.
              </p>
              <p className="text-xs text-muted-foreground">
                Logged in as: <span className="font-mono">{user.email}</span>
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
